import { Component, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { PDFDocument, rgb, PDFTextField, PDFCheckBox, StandardFonts } from 'pdf-lib';
import { DentalCredentialsService } from '../../services/dental-credentials.service';
import { DocumentCategory, DocumentSubCategory, DocumentSubCategoryName, Specialities } from '../../../../shared/constants/document.constants';
import { v4 as uuidv4 } from 'uuid';
import { ButtonComponent, CheckboxComponent, TextInputComponent } from 'ddmo-storybook-lib';
import { SharedService } from '../../../../shared/services/shared.service';
import { PDF_SCHEMAS } from './pdf-schemas';
import { MatDialog } from '@angular/material/dialog';
import { PopupComponent } from '../popup/popup.component';
import { LoaderService } from '../../../../shared/services/loader.service';
import { DocumentTypes } from '../signed-documents/signed-documents.component';

/* ============================================================
 * Constants & Types
 * ============================================================
 */

const DEBUG = false; // toggle component logs on/off
/** Initial overlay footprint in CSS pixels */
const DEFAULT_SIGNATURE = {
    BASE_WIDTH: 200,
    BASE_HEIGHT: 60,
};

/** Supported signature kinds (typed text, uploaded/drawn image) */
type SignatureType = 'drawn' | 'typed' | 'uploaded';

/** Overlay model for a placed signature */
interface PlacedSignature {
    type: SignatureType;
    data: string;          // dataURL for image, plain string for typed
    x: number;             // left/top in page-local CSS px
    y: number;
    width: number;         // current overlay width/height in CSS px
    height: number;
    baseWidth: number;     // footprint at scale = 1
    baseHeight: number;
    isDragging: boolean;
    isResizing: boolean;
    isFixed: boolean;
    pageIndex?: number;    // 0-based page index
    xPdf?: number;         // optional: not needed when we compute from DOM
    yPdf?: number;
}

/** Cache for AcroForm metadata (field widgets) */
interface AcroCache {
    objects: Record<string, any[]>;
    names: string[];
}

interface RequiredSomField {
    key: string;   // SOM path
    label: string; // Human readable name
}

const W9_ALWAYS_REQUIRED_FIELDS: RequiredSomField[] = [
    { key: 'topmostSubform[0].Page1[0].f1_1[0]', label: 'First Name' },
    { key: 'topmostSubform[0].Page1[0].Address[0].f1_7[0]', label: 'Address Line 1' },
    { key: 'topmostSubform[0].Page1[0].Address[0].f1_8[0]', label: 'City State' },
];

const W9_FEDERAL_CLASSIFICATION_KEYS = [
    'topmostSubform[0].Page1[0].FederalClassification[0].c1_1[0]',
    'topmostSubform[0].Page1[0].FederalClassification[0].c1_1[1]',
    'topmostSubform[0].Page1[0].FederalClassification[0].c1_1[2]',
    'topmostSubform[0].Page1[0].FederalClassification[0].c1_1[3]',
    'topmostSubform[0].Page1[0].FederalClassification[0].c1_1[4]',
    'topmostSubform[0].Page1[0].FederalClassification[0].c1_1[5]',
    'topmostSubform[0].Page1[0].FederalClassification[0].c1_7[0]',
];

const W9_SSN_KEYS = [
    'topmostSubform[0].Page1[0].SSN[0].f1_11[0]',
    'topmostSubform[0].Page1[0].SSN[0].f1_12[0]',
    'topmostSubform[0].Page1[0].SSN[0].f1_13[0]',
];

const W9_EIN_KEYS = [
    'topmostSubform[0].Page1[0].EmployerID[0].f1_14[0]',
    'topmostSubform[0].Page1[0].EmployerID[0].f1_15[0]',
];

let PA_REQUIRED_FIELDS: RequiredSomField[] = [
    { key: 'Dentist name please print', label: 'Dentist Name' },
    { key: 'Date of birth', label: 'Date Of Birth' },
    { key: 'Street address', label: 'Street address' },
    { key: 'City', label: 'City' },
    { key: 'State', label: 'State' },
    { key: 'Zip code', label: 'Zipcode' },
    { key: 'Phone', label: 'Phone' },
    { key: 'SSN or tax ID', label: 'SSN or Tax Id' },
    { key: 'MO state license', label: 'MO state license' },
    { key: 'NPI type 1', label: 'NPI Type 1' },
]

const PATCH_VALUES: Record<string, any> = {
};

const READ_ONLY_SOM_FIELDS = [
    'dhFormfield-6123081579', // Approved by
    'dhFormfield-6123081707', // Approved by Date
];

const FIELD_VALIDATIONS: Record<string, FieldValidation> = {
    'Date of birth': {
        pattern: /^(0?[1-9]|1[0-2])[-\/](0?[1-9]|[12][0-9]|3[01])[-\/]\d{4}$/,
        label: 'Date Of Birth',
        message: 'Date of Birth must be in MM/DD/YYYY format.'
    },
};

type FieldValidation = {
    pattern: RegExp;
    label: string;
    message: string;
};

const DOCUMENT_TITLES = {
    W9: 'W9',
    ParticipatingAgreement: 'participatingAgreement',
    UniformRequirements: 'uniformRequirements',
    Default: 'Document'
};

const Headings = {
    W9: 'W9',
    participatingAgreement: 'Participating Agreement',
    uniformRequirements: 'Uniform Requirements',
    Default: 'Document'
}

const SIGNATURE_MAX_LENGTH = 90;
const LEGAL_NAME_PATTERN = /^[A-Za-z][A-Za-z'.-]*(?:\s+[A-Za-z][A-Za-z'.-]*)*$/;

/* ============================================================
 * Component
 * ============================================================
 */

@Component({
    selector: 'app-pdf-signing',
    imports: [CommonModule, FormsModule, PdfViewerModule, ButtonComponent, TextInputComponent, ReactiveFormsModule, CheckboxComponent],
    templateUrl: './pdf-signing.component.html',
    styleUrl: './pdf-signing.component.scss',
})
export class PdfSigningComponent implements AfterViewInit, OnDestroy {
    canEnableSignatureInput: boolean = false;
    // Template refs
    @ViewChild('signatureCanvas', { static: true }) signatureCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('pdfContainer', { static: true }) pdfContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('signatureOverlay', { static: false }) signatureOverlay?: ElementRef<HTMLDivElement>;

    // Doc routing info
    documentTitle: any = '';
    basePath: any = '/assets/pdf';
    uniformRequirements: string = `${this.basePath}/ddmo_uniform_requirements.pdf`;
    participatingAgreement: string = `${this.basePath}/participating_agreement.pdf`;
    w9Form: string = `${this.basePath}/w9_form.pdf`;

    // Signature inputs
    isDrawing = false;
    typedSignature = '';
    signatureFile: File | null = null;
    signatureFileUrl: string | null = null;
    signatureScale = 1;

    // Auto-patch configuration
    autoPlacementConfig = {
        alignment: 'bottom-right' as 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'custom',
        marginX: 50,
        marginY: 50
    };

    // PDF.js & forms state
    pdfDoc?: any; // PDFDocumentProxy
    fieldIndexById = new Map<string, { name: string; type: string }>();

    // Signature overlay model
    placedSignature: PlacedSignature | any = null;
    sigData: any;
    // Drawing internals
    private ctx: CanvasRenderingContext2D | null = null;
    private lastX = 0;
    private lastY = 0;

    // PDF render state
    private pdfReady = false;
    private validationListenersBound = false;
    private autoPatchedSomKeys = new Set<string>();
    private autoPatchedAcroNames = new Set<string>();
    private autoPatchedWidgetIds = new Set<string>();
    private autoLockObserver?: MutationObserver;

    // Drag helpers
    private dragOffset = { x: 0, y: 0 };

    // Cached Acro metadata
    private acroCache?: AcroCache;
    private originalPdfBlob: Blob | null = null;
    pdfSource: any;
    isPdfLoading: boolean = true;
    documentsMap: any;
    docData: any = {};
    fileName: any;
    baseFileName: any;
    readonly expectedSignerName = this.resolveExpectedProviderSignatureName();
    readonly isSignerNameAutoResolved = !!this.expectedSignerName;

    public inputForm = this.formBuilder.group({
        signText: [{ value: this.expectedSignerName, disabled: this.isSignerNameAutoResolved }, [Validators.required, Validators.minLength(2), Validators.maxLength(SIGNATURE_MAX_LENGTH), Validators.pattern(LEGAL_NAME_PATTERN)]]
    });

    customErrorMessages = {
        required: 'Please enter your full legal name.',
        minlength: 'Please enter a minimum of 2 characters.',
        maxlength: `Signature cannot exceed ${SIGNATURE_MAX_LENGTH} characters.`,
        pattern: 'Please enter first and last name using letters only.'
    };

    isSignatureFixed: boolean = false;
    impersonatedUser: any = localStorage.getItem("impersonatedUser") ?? ""
    DocumentTypes = DocumentTypes;
    isFlattening: boolean = false;
    termscheckForm = this.fb.group({
        Attestation: [{ value: false, disabled: false }, Validators.requiredTrue]
    });
    checkboxLabel = 'I have read and reviewed the Uniform Requirements.';
    isPdfRead = false;
    showErrorMessage = false;
    documentId: any;

    constructor(private router: Router, private route: ActivatedRoute, private formBuilder: FormBuilder,
        private dentalCredentialingService: DentalCredentialsService, private sharedService: SharedService,
        public dialog: MatDialog, private datePipe: DatePipe, private loaderService: LoaderService,
        private fb: FormBuilder,) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.route.queryParams.subscribe((params: any) => {
            const modifiedParams = { ...params };

            modifiedParams['id'] = modifiedParams['title'] === 'W9'
                ? DocumentTypes.W9
                : (modifiedParams['title'] === 'Participating agreement'
                    ? DocumentTypes.ParticipatingAgreement
                    : DocumentTypes.UniformRequirements);

            this.documentId = modifiedParams['id'];

            const FILE_NAMES: any = {
                uniformRequirements: 'ddmo_uniform_requirements.pdf',
                participatingAgreement: 'participating_agreement.pdf',
                W9: 'w9_form.pdf'
            };

            this.documentTitle = modifiedParams['id']
                ? (modifiedParams['id'] === DocumentTypes.W9
                    ? DOCUMENT_TITLES.W9
                    : (modifiedParams['id'] === DocumentTypes.ParticipatingAgreement
                        ? DOCUMENT_TITLES.ParticipatingAgreement
                        : DOCUMENT_TITLES.UniformRequirements))
                : DOCUMENT_TITLES.Default;

            this.documentsMap = JSON.parse(localStorage.getItem("signedDocuments")!) || {};

            this.docData = this.documentsMap[this.documentTitle]?.[0] || {};

            const originalFileName = this.docData?.originalFileName?.split(".")[0];
            this.fileName = this.documentTitle === DOCUMENT_TITLES.UniformRequirements
                ? FILE_NAMES.uniformRequirements
                : (this.documentTitle === DOCUMENT_TITLES.ParticipatingAgreement
                    ? (originalFileName || FILE_NAMES.participatingAgreement) : (originalFileName || FILE_NAMES.W9));

            this.isSignatureFixed = this.docData?.isSigned;
            this.isFlattening = this.docData?.isSigned;
            this.baseFileName = FILE_NAMES[this.documentTitle] || FILE_NAMES.W9;
            this.onViewDocument(this.fileName, true);
            this.patchDefaultValues();
            if (this.impersonatedUser) {
                this.inputForm.disable();
            }
        });

    }

    patchDefaultValues() {
        let credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
        //w9 fields
        const demographics = JSON.parse(localStorage.getItem('demographics')!);
        const credentials = JSON.parse(localStorage.getItem('credential')!);
        const loggedUserDetails = JSON.parse(localStorage.getItem('loggedUserDetails')!);
        const locations = JSON.parse(localStorage.getItem('locations')!);
        const isPrimaryLocation = (location: any): boolean => {
            const rawValue = location?.isPrimary;
            if (rawValue === true || rawValue === 1) {
                return true;
            }
            const normalizedValue = `${rawValue ?? ''}`.trim().toLowerCase();
            return normalizedValue === 'yes' || normalizedValue === 'true' || normalizedValue === '1';
        };
        let primaryLocation = Array.isArray(locations) ? locations.find((loc: any) => isPrimaryLocation(loc)) : null;
        if (!primaryLocation && Array.isArray(locations) && locations.length > 0) {
            primaryLocation = locations[0];
        }
        primaryLocation = Array.isArray(primaryLocation) ? primaryLocation[0] : primaryLocation;

        if (localStorage.getItem('documents')?.length) {
            const documents = JSON.parse(localStorage.getItem('documents')!);
            const signedDocuments = documents.filter((doc: any) =>
                doc.categoryId === DocumentCategory.SignedDocument && doc.subCategoryId === DocumentSubCategory.UniformRequirements);

            if (signedDocuments?.length > 0) {
                this.isPdfRead = true;
                this.termscheckForm.get('Attestation')?.setValue(true)
                this.termscheckForm.get('Attestation')?.updateValueAndValidity();
            }
        }

        PA_REQUIRED_FIELDS = PA_REQUIRED_FIELDS.filter(
            field => field.key !== 'Specialty license'
        );
        if (loggedUserDetails.licenseState == 'SC') {
            if (demographics['specialty'] && demographics['specialty'].toString() !== Specialities.GeneralDentistry.toString()) { // validating general dentistry
                PA_REQUIRED_FIELDS.push(
                    { key: 'Specialty license', label: 'Speciality license' },
                )
            }
        }

        const demographicLastName = demographics['lastName'] || '';
        const fullname = `${demographics['firstName'] || ''} ${demographicLastName}`.trim()
            || `${loggedUserDetails['firstName'] || ''} ${loggedUserDetails['lastName'] || ''}`.trim();

        PATCH_VALUES['topmostSubform[0].Page1[0].f1_1[0]'] = fullname;
        PATCH_VALUES['topmostSubform[0].Page1[0].Address[0].f1_7[0]'] = demographics ? `${demographics['address1'] || ''} ${demographics['address2'] || ''}` : '';
        PATCH_VALUES['topmostSubform[0].Page1[0].Address[0].f1_8[0]'] = demographics ? `${demographics['city'] || ''} ${demographics['state'] || ''} ${demographics['zip'] || ''}` : '';
        if (demographics['SSN']) {
            const ssn = demographics['SSN'];
            PATCH_VALUES['topmostSubform[0].Page1[0].SSN[0].f1_11[0]'] = `${ssn[0] || ''}${ssn[1] || ''}${ssn[2] || ''}` || '';
            PATCH_VALUES['topmostSubform[0].Page1[0].SSN[0].f1_12[0]'] = `${ssn[3] || ''}${ssn[4] || ''}` || '';
            PATCH_VALUES['topmostSubform[0].Page1[0].SSN[0].f1_13[0]'] = `${ssn[5] || ''}${ssn[6] || ''}${ssn[7] || ''}${ssn[8] || ''}` || '';
        }

        //participating agreement
        PATCH_VALUES['Dentist name please print'] = fullname;
        PATCH_VALUES['Date of birth'] = demographics ? `${this.formatDate(demographics['DOB']) || ''}` : '';
        PATCH_VALUES['Street address'] = primaryLocation ? `${primaryLocation['address1'] || ''} ${primaryLocation['address2'] || ''}`.trim() || `${primaryLocation['address'] || ''}`.trim() : '';
        PATCH_VALUES['City'] = primaryLocation ? `${primaryLocation['city'] || ''}` : '';
        PATCH_VALUES['State'] = primaryLocation ? `${primaryLocation['state'] || ''}` : '';
        PATCH_VALUES['Zip code'] = primaryLocation ? `${primaryLocation['zip'] || ''}` : '';
        PATCH_VALUES['Phone'] = primaryLocation ? this.formatPhoneNumber(primaryLocation['voicePhone']) : '';
        PATCH_VALUES['SSN or tax ID'] = primaryLocation ? `${primaryLocation['tin'] || ''}` : '';
        PATCH_VALUES['NPI type 1'] = demographics ? `${credentialPayload['npi'] || ''}` : '';
        const payees = JSON.parse(localStorage.getItem('payees') || '[]');
        const findMatchingPayeeForLocation = (location: any) => {
            if (!location || !Array.isArray(payees) || !payees.length) {
                return {};
            }
            const locationPayeeId = `${location?.payeeId ?? location?.payeeID ?? ''}`.trim();
            const locationId = `${location?.locationId ?? location?.esLocationId ?? ''}`.trim();
            const locationName = `${location?.locationName ?? location?.name ?? ''}`.trim().toLowerCase();
            const locationAddress1 = `${location?.address1 ?? location?.address ?? ''}`.trim().toLowerCase();
            const locationZip = `${location?.zip ?? ''}`.trim();

            return (
                payees.find((payee: any) => `${payee?.payeeId ?? payee?.payeeID ?? ''}`.trim() === locationPayeeId) ||
                payees.find((payee: any) => `${payee?.locationId ?? payee?.esLocationId ?? ''}`.trim() === locationId) ||
                payees.find((payee: any) => `${payee?.payeeName ?? ''}`.trim().toLowerCase() === locationName) ||
                payees.find((payee: any) =>
                    `${payee?.payeeAddress1 ?? payee?.payeeLocation ?? payee?.address1 ?? payee?.address ?? ''}`.trim().toLowerCase() === locationAddress1 &&
                    `${payee?.payeeZip ?? payee?.zip ?? ''}`.trim() === locationZip
                ) ||
                (payees.length === 1 ? payees[0] : {})
            );
        };
        const matchedPrimaryPayee: any = findMatchingPayeeForLocation(primaryLocation);
        const payeeNpi = `${matchedPrimaryPayee?.payeeNPI ?? matchedPrimaryPayee?.npi ?? ''}`;
        PATCH_VALUES['NPI type 2'] = (payeeNpi?.trim())
            ? `${payeeNpi}`
            : '';

        PATCH_VALUES['Address'] = '';
        PATCH_VALUES['City_2'] = '';
        PATCH_VALUES['State_2'] = '';
        PATCH_VALUES['Zip code_2'] = '';
        PATCH_VALUES['Phone_2'] = '';
        const tinDetails = JSON.parse(localStorage.getItem('TINDetails') || '[]');

        const getPayeeAddressForLocation = (location: any) => {
            if (!location) {
                return { address1: '', address2: '', city: '', state: '', zip: '' };
            }
            const matchingPayee: any = findMatchingPayeeForLocation(location);
            const locationLookupIds = [location?.locationId, location?.esLocationId]
                .filter((id: any) => id !== undefined && id !== null && `${id}`.trim() !== '')
                .map((id: any) => `${id}`.trim());
            const matchingTinDetail: any = Array.isArray(tinDetails)
                ? (tinDetails.find((item: any) => {
                    const itemIds = [item?.locationId, item?.esLocationId]
                        .filter((id: any) => id !== undefined && id !== null && `${id}`.trim() !== '')
                        .map((id: any) => `${id}`.trim());
                    return locationLookupIds.length > 0 && itemIds.some((id: string) => locationLookupIds.includes(id));
                }) || {})
                : {};
            const hasTinDetailPayeeAddress = !!(
                `${matchingTinDetail?.payeeAddressLine1 ?? matchingTinDetail?.payeeAddress1 ?? matchingTinDetail?.payeeAddress ?? ''}`.trim() ||
                `${matchingTinDetail?.payeeAddressLine2 ?? matchingTinDetail?.payeeAddress2 ?? ''}`.trim() ||
                `${matchingTinDetail?.payeeCity ?? ''}`.trim() ||
                `${matchingTinDetail?.payeeState ?? ''}`.trim() ||
                `${matchingTinDetail?.payeeZip ?? ''}`.trim()
            );
            const resolvedPayeeSource: any = hasTinDetailPayeeAddress
                ? matchingTinDetail
                : matchingPayee;

            return {
                address1: `${resolvedPayeeSource?.payeeAddressLine1 ?? resolvedPayeeSource?.payeeAddress1 ?? resolvedPayeeSource?.payeeAddress ?? resolvedPayeeSource?.payeeLocation ?? resolvedPayeeSource?.address1 ?? resolvedPayeeSource?.address ?? location?.payeeAddressLine1 ?? location?.payeeAddress1 ?? location?.payeeLocation ?? ''}`.trim(),
                address2: `${resolvedPayeeSource?.payeeAddressLine2 ?? resolvedPayeeSource?.payeeAddress2 ?? location?.payeeAddressLine2 ?? location?.payeeAddress2 ?? ''}`.trim(),
                city: `${resolvedPayeeSource?.payeeCity ?? resolvedPayeeSource?.city ?? location?.payeeCity ?? ''}`.trim(),
                state: `${resolvedPayeeSource?.payeeState ?? resolvedPayeeSource?.state ?? location?.payeeState ?? ''}`.trim(),
                zip: `${resolvedPayeeSource?.payeeZip ?? resolvedPayeeSource?.zip ?? location?.payeeZip ?? ''}`.trim()
            };
        };

        const primaryPayee = getPayeeAddressForLocation(primaryLocation);

        const primaryAddress1 = `${primaryLocation?.address1 ?? primaryLocation?.address ?? ''}`.trim();
        const primaryAddress2 = `${primaryLocation?.address2 ?? ''}`.trim();
        const primaryCity = `${primaryLocation?.city ?? ''}`.trim();
        const primaryState = `${primaryLocation?.state ?? ''}`.trim();
        const primaryZip = `${primaryLocation?.zip ?? ''}`.trim();
        const normalizeAddressPart = (value: string) => value.replace(/\s+/g, ' ').trim().toLowerCase();
        const isPayeeDifferentFromPrimary =
            !!(primaryPayee.address1 || primaryPayee.address2 || primaryPayee.city || primaryPayee.state || primaryPayee.zip) &&
            (
                normalizeAddressPart(primaryPayee.address1) !== normalizeAddressPart(primaryAddress1) ||
                normalizeAddressPart(primaryPayee.address2) !== normalizeAddressPart(primaryAddress2) ||
                normalizeAddressPart(primaryPayee.city) !== normalizeAddressPart(primaryCity) ||
                normalizeAddressPart(primaryPayee.state) !== normalizeAddressPart(primaryState) ||
                normalizeAddressPart(primaryPayee.zip) !== normalizeAddressPart(primaryZip)
            );
        const formatAddressWithCommas = (address: { address1: string; address2: string; city: string; state: string; zip: string }) => {
            const line1 = [address.address1, address.address2].filter((part: string) => !!part).join(', ');
            const cityStateZip = [address.city, [address.state, address.zip].filter((part: string) => !!part).join(' ')].filter((part: string) => !!part).join(', ');
            return [line1, cityStateZip].filter((part: string) => !!part).join(', ').replace(/\s+/g, ' ').trim();
        };
        PATCH_VALUES['Payment office address If different than primary service office above'] = isPayeeDifferentFromPrimary
            ? formatAddressWithCommas(primaryPayee)
            : '';
        PATCH_VALUES['Payment office for additional address If different than above'] = '';
        PATCH_VALUES['Payment office for additional address (If different than above)'] = '';

        if (credentials?.length) {
            PATCH_VALUES['MO state license'] = credentials ? `${credentials[0]['stateLicenseNumber'] || ''}` : '';
            PATCH_VALUES['Specialty license'] = credentials ? `${credentials[0]['specialtyLicenseNumber'] || ''}` : '';
        }

        this.captureAutoPatchedKeys();
    }

    get getDocumentTitle(): any {
        return Headings[this.documentTitle as keyof typeof Headings]
    }

    async disableApprovedFields() {
        const cache = await this.ensureAcroCache();
        if (!cache || !this.pdfDoc?.annotationStorage) return;

        for (const somKey of READ_ONLY_SOM_FIELDS) {
            const acroName = await this.somToAcroName(somKey);
            const widgets = await this.findWidgetsByName(acroName);

            for (const w of widgets || []) {
                // Set readOnly flag in annotation storage
                this.pdfDoc.annotationStorage.setValue(w.id, {
                    readOnly: true,
                });

            }
        }
    }

    onViewDocument(doc: any, isView: boolean = false): any {
        let filename = typeof doc == 'string' ? doc : doc.originalFileName;
        if (doc?.name == `uniformRequirements.pdf`) {
            filename = 'ddmo_uniform_requirements.pdf';
        }
        if (isView) {
            this.isPdfLoading = true;
            this.loaderService.show();
        }
        this.dentalCredentialingService.viewAzureDocument((this.docData && filename != `ddmo_uniform_requirements.pdf`) ?
            isView ? filename : filename.split(".")[0] : filename).subscribe({
                next: (response: any) => {
                    if (isView) {
                        this.originalPdfBlob = response.body;
                        this.pdfSource = window.URL.createObjectURL(response.body);
                    }
                    else {
                        const url = window.URL.createObjectURL(response.body);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = ((this.docData?.isSigned && doc?.name != `uniformRequirements.pdf`) ?
                            'Signed_' : '') + `${typeof doc == 'string' ? doc : doc.name}`;
                        link.click();
                        window.URL.revokeObjectURL(url);
                    }
                }, error: (err: any) => {
                    if (isView) {
                        this.isPdfLoading = false;
                        this.loaderService.hide();
                    }
                    this.sharedService.logError("APIFailed", "FilePdf-SigningComponent - viewAzureDocument, Method:onViewDocument", this.router.url.split('?')[0], err).subscribe();
                }
            })
    }

    private async patchAfterLoad() {
        if (!this.pdfReady || !this.pdfDoc) {
            this.log('PDF not ready yet. Delaying patch...');
            return;
        }

        await this.indexFormFields();
        await this.applyAcroPatch(PATCH_VALUES);
        await this.disableAutoPatchedFields();

        // Re-apply lock once more after viewer annotations finish rendering.
        setTimeout(() => {
            this.disableAutoPatchedFields();
        }, 500);

        this.log('PATCH_VALUES applied successfully.');
    }

    formatDate(date: any): string {
        if (!date) {
            return '';
        }

        const value = String(date);

        // Handle YYYYMMDD
        if (/^\d{8}$/.test(value)) {
            return `${value.substring(4, 6)}/${value.substring(6, 8)}/${value.substring(0, 4)}`;
        }
        // Avoid Date parsing to prevent timezone day shift
        const yyyyMmDd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (yyyyMmDd) {
            return `${yyyyMmDd[2]}/${yyyyMmDd[3]}/${yyyyMmDd[1]}`;
        }

        const parsedDate = new Date(value);

        if (!isNaN(parsedDate.getTime())) {
            return this.datePipe.transform(parsedDate, 'MM/dd/yyyy') || '';
        }

        return value;
    }

    formatPhoneNumber(phone: string): string {
        if (!phone) {
            return '';
        }

        const cleaned = phone.replace(/\D/g, '');

        if (cleaned.length === 10) {
            return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
        }

        return phone;
    }

    async saveDocument(file: any = null, skipValidation: boolean = false) {
        try {
            this.loaderService.show();
            const loginUserId = localStorage.getItem("loggedUserId");
            const subCategoryId = this.documentTitle == DOCUMENT_TITLES.UniformRequirements ?
                DocumentSubCategory.UniformRequirements :
                this.documentTitle == DOCUMENT_TITLES.ParticipatingAgreement ? DocumentSubCategory.ParticipatingAgreement :
                    DocumentSubCategory.W9Form;
            let credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
            let date = new Date();

            let year = date.getFullYear();
            let month = String(date.getMonth() + 1).padStart(2, '0');
            let day = String(date.getDate()).padStart(2, '0');
            const uniqueId = uuidv4();
            const filename = `${year}${month}${day}_${credentialPayload['npi']}_${uniqueId}`;

            const params = {
                "credentialingEventId": localStorage.getItem('credentialingEventId'),
                "categoryId": DocumentCategory.SignedDocument,
                "subCategoryId": subCategoryId,
                "fileName": filename,
                "isSigned": this.isSignatureFixed || this.documentTitle == DOCUMENT_TITLES.UniformRequirements || false,
                "userId": loginUserId || -1
            }
            if (!this.docData || typeof this.docData !== 'object') {
                this.docData = {};
            }
            this.docData['originalFileName'] = `${filename}.pdf`;
            file = this.documentTitle == DOCUMENT_TITLES.UniformRequirements ?
                await this.generateFile(filename) : await this.downloadSignedPdfWithSignature(skipValidation);
            if (!file && subCategoryId != DocumentSubCategory.UniformRequirements) throw Error("File not uploaded!");

            const response = await this.dentalCredentialingService.saveDocument(file, params);

            const documents = JSON.parse(localStorage.getItem('documents') || '[]');

            const payload = {
                documentId: response.documentId,
                category: 'SignedDocument',
                categoryId: DocumentCategory.SignedDocument,
                credentialingEventId: localStorage.getItem('credentialingEventId'),
                fileName: filename,
                isSigned: this.isSignatureFixed || this.documentTitle == DOCUMENT_TITLES.UniformRequirements || false,
                subCategoryId: subCategoryId,
                subcategory: DocumentSubCategoryName[subCategoryId]
            };

            const index = documents.findIndex(
                (item: any) => item.subCategoryId === subCategoryId
            );

            if (index !== -1) {
                documents.splice(index, 1);
            }

            documents.push(payload);
            localStorage.setItem('documents', JSON.stringify(documents));
            return true;
        } catch (error: any) {
            console.error(error?.message);
            throw new Error(error?.message);
        } finally {
            this.loaderService.hide();
        }
    }

    async onDeleteDocument(doc: any) {
        this.loaderService.show();
        const loginUserId = localStorage.getItem("loggedUserId");
        const credentialingEventId = localStorage.getItem('credentialingEventId');
        const requestParams = {
            "credentialingEventID": Number(credentialingEventId),
            "fileName": doc.originalFileName.split(".")[0],
            "modifiedUserID": loginUserId ? Number(loginUserId) : 0
        }
        this.dentalCredentialingService.deleteDocument(requestParams).subscribe({
            next: (response: any) => {
                this.documentsMap[this.documentTitle] = [];
                this.docData = null;
                this.isSignatureFixed = false;
                const documents = JSON.parse(localStorage.getItem('documents') || '[]');
                const subCategoryId = this.documentTitle == DOCUMENT_TITLES.UniformRequirements ?
                    DocumentSubCategory.UniformRequirements :
                    this.documentTitle == DOCUMENT_TITLES.ParticipatingAgreement ? DocumentSubCategory.ParticipatingAgreement :
                        DocumentSubCategory.W9Form;
                const _index = documents.findIndex(
                    (item: any) => item.subCategoryId === subCategoryId
                );

                if (_index !== -1) {
                    documents.splice(_index, 1);
                }
                localStorage.setItem('documents', JSON.stringify(documents));
                this.onViewDocument(this.baseFileName, true);
                setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
                this.loaderService.hide();
            }, error: (err: any) => {
                this.loaderService.hide();
            }
        })
    }

    ngAfterViewInit() {
        if (this.signatureCanvas) {
            this.ctx = this.signatureCanvas.nativeElement.getContext('2d');
            if (this.ctx) {
                this.ctx.strokeStyle = '#000';
                this.ctx.lineWidth = 2;
                this.ctx.lineCap = 'round';
            }
        }
        this.inputForm.get('signText')?.disable();
    }

    ngOnDestroy(): void {
        this.autoLockObserver?.disconnect();
        this.autoLockObserver = undefined;
    }

    private isReadGateSatisfiedForSignature(): boolean {
        // Uniform requirements use attestation/read flow; other docs are gated by required fields only.
        return this.documentId === DocumentTypes.UniformRequirements ? this.isPdfRead : true;
    }

    async updateSignatureInputState() {
        const requiredResult = await this.validateRequiredFields();
        this.canEnableSignatureInput =
            this.isReadGateSatisfiedForSignature() && requiredResult.valid;
        const signControl = this.inputForm.get('signText');

        if (this.isSignerNameAutoResolved) {
            signControl?.setValue(this.expectedSignerName, { emitEvent: false });
            signControl?.disable({ emitEvent: false });
        } else if (this.canEnableSignatureInput && !this.impersonatedUser) {
            signControl?.enable({ emitEvent: false });
        } else {
            signControl?.disable({ emitEvent: false });
        }
        return requiredResult;
    }

    /* ------------------------------------------------------------
     * Logging
     * ------------------------------------------------------------
     */

    private log(label: string, ...args: any[]) {
        if (DEBUG) {
            console.log(`[PdfSigning] ${label}`, ...args);
        }
    }

    private async logAvailablePdfFields() {
        if (!DEBUG || !this.pdfDoc?.getFieldObjects) return;

        try {
            const fieldObjects = await this.pdfDoc.getFieldObjects();
            const names = Object.keys(fieldObjects || {});

            this.log(`Discovered ${names.length} PDF field names`);
            console.log('[PdfSigning] PDF field names:', names);

            const widgetRows: any[] = [];
            Object.entries(fieldObjects || {}).forEach(([name, widgets]: any) => {
                (widgets || []).forEach((w: any) => {
                    widgetRows.push({
                        fieldName: name,
                        widgetId: w?.id,
                        type: w?.type,
                        exportValue: w?.exportValues,
                    });
                });
            });

            if (widgetRows.length) {
                console.table(widgetRows);
            }

            const mappingRows = Object.keys(PATCH_VALUES).map((somKey) => {
                const acroName = somKey.match(/([^.[]+)\[\d+\]$/)?.[1] ?? somKey;
                const hasExact = names.includes(acroName);
                const hasEndsWith = !hasExact && names.some((n) => n.endsWith(acroName));
                const hasContains = !hasExact && !hasEndsWith && names.some((n) => n.includes(acroName));

                return {
                    somKey,
                    derivedAcroName: acroName,
                    matchFound: hasExact || hasEndsWith || hasContains,
                    matchType: hasExact ? 'exact' : hasEndsWith ? 'endsWith' : hasContains ? 'contains' : 'none',
                };
            });

            if (mappingRows.length) {
                console.table(mappingRows);
            }
        } catch (error) {
            this.log('logAvailablePdfFields failed', error);
        }
    }

    /* ------------------------------------------------------------
     * PDF load hook (from ng2-pdf-viewer)
     * ------------------------------------------------------------
     */

    async onPdfLoaded(pdf: any) {
        this.pdfReady = true;
        this.pdfDoc = pdf;

        await this.indexFormFields();
        // await this.logAvailablePdfFields(); //pdf form fields

        // ✅ Always patch values after load
        await this.patchAfterLoad();

        // ✅ If document already signed → disable fields
        if ((this.docData?.isSigned || this.impersonatedUser) && !this.isFlattening) {
            this.isFlattening = true;
            setTimeout(async () => {
                await this.flattenLoadedPdf();
            }, 800);
        }

        await this.disableApprovedFields();
        this.hardDisableApprovedFieldsInDom();

        this.reparentOverlayIfNeeded();
        setTimeout(() => {
            const inner = this.pdfContainer.nativeElement.querySelector('.ng2-pdf-viewer-container');

            if (inner) {
                inner.addEventListener('scroll', this.onPdfScroll.bind(this));
            }

            this.bindValidationListeners();
        }, 300);

        setTimeout(() => {
            this.updateSignatureInputState();
        }, 500);
        this.enableAutoFormatting();
        this.isPdfLoading = false;
        this.loaderService.hide();
    }

    private bindValidationListeners() {
        if (this.validationListenersBound || !this.pdfContainer?.nativeElement) {
            return;
        }

        const container = this.pdfContainer.nativeElement;
        const refreshState = () => {
            this.updateSignatureInputState();
        };

        container.addEventListener('input', refreshState, true);
        container.addEventListener('change', refreshState, true);

        // Prevent edits on auto-patched fields even if PDF.js re-renders DOM nodes.
        const blockLockedFieldEdit = (event: Event) => {
            const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
            if (!target) return;

            const name = target.getAttribute('name') || '';
            const annotationHost = (target as HTMLElement).closest('[data-annotation-id]') as HTMLElement | null;
            const annotationId = annotationHost?.getAttribute('data-annotation-id') || '';
            const isLockedByName = !!name && this.autoPatchedAcroNames.has(name);
            const isLockedByWidget = !!annotationId && this.autoPatchedWidgetIds.has(annotationId);
            const isLockedByClass = (target as HTMLElement).classList.contains('auto-patched-locked-field');
            if (!isLockedByName && !isLockedByWidget && !isLockedByClass) return;

            event.preventDefault();
            event.stopPropagation();
            target.blur();
            this.applyAutoPatchedDomLocks();
        };

        container.addEventListener('beforeinput', blockLockedFieldEdit, true);
        container.addEventListener('keydown', blockLockedFieldEdit, true);
        container.addEventListener('paste', blockLockedFieldEdit, true);

        this.validationListenersBound = true;
    }

    private async flattenLoadedPdf() {
        if (!this.originalPdfBlob) return;

        const buffer = await this.originalPdfBlob.arrayBuffer();
        const pdfDoc = await PDFDocument.load(buffer);

        try {
            const unifiedValues = await this.getUnifiedFormValues();
            await this.normalizeFlattenTextFieldAppearance(pdfDoc, unifiedValues);
            pdfDoc.getForm().flatten();
        } catch (e) { }

        const bytes: any = await pdfDoc.save();
        const blob = new Blob([bytes], { type: 'application/pdf' });
        this.pdfSource = URL.createObjectURL(blob);
    }

    private async normalizeFlattenTextFieldAppearance(pdfDoc: PDFDocument, fallbackValues: Record<string, any> = {}) {
        try {
            const form = pdfDoc.getForm();
            const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);

            // Match corp-authority flatten behavior without provider usage.
            const targetFontSize = 11;

            for (const field of form.getFields()) {
                if (!(field instanceof PDFTextField)) continue;

                const fieldName = field.getName();
                const somName = Object.keys(fallbackValues).find((k) =>
                    k === fieldName ||
                    k.endsWith(`.${fieldName}[0]`) ||
                    k.endsWith(`.${fieldName}`) ||
                    k === `${fieldName}[0]`
                );
                const derivedAcroName = somName ? await this.somToAcroName(somName) : fieldName;

                const candidates = [
                    fieldName,
                    somName,
                    derivedAcroName,
                    somName ? somName.split('.').pop() : undefined,
                ].filter(Boolean) as string[];

                // Prefer whatever is already in the field; fall back to merged values and PATCH_VALUES.
                let value: any = field.getText?.();
                for (const key of candidates) {
                    if (value !== undefined && value !== null && String(value).trim() !== '') break;
                    value = fallbackValues[key] ?? PATCH_VALUES[key];
                }

                if (value === undefined || value === null || String(value).trim() === '') {
                    continue;
                }

                const strVal = String(value);
                if (strVal.startsWith('data:image')) {
                    continue;
                }

                let fontSize = targetFontSize;
                const acroField: any = (field as any).acroField;
                const widgets: any[] = acroField?.getWidgets?.() || [];

                for (const widget of widgets) {
                    try {
                        const rect = widget.getRectangle?.();
                        if (!rect || rect.width <= 0 || rect.height <= 0) {
                            continue;
                        }

                        if (strVal) {
                            const textWidth = helvetica.widthOfTextAtSize(strVal, fontSize);
                            const availableWidth = Math.max(10, rect.width - 4);
                            if (textWidth > availableWidth) {
                                fontSize = Math.max(7.5, (availableWidth / textWidth) * targetFontSize);
                            }
                        }

                        // Nudge short single-line widgets so flattened text sits closer to the underline.
                        if (rect.height <= 35 && typeof widget.setRectangle === 'function') {
                            const bottomGap = 1.5;
                            const targetBoxHeight = Math.min(rect.height, fontSize + 3);
                            widget.setRectangle({
                                x: rect.x,
                                y: rect.y + bottomGap - 2.5,
                                width: rect.width,
                                height: targetBoxHeight,
                            });
                        }
                    } catch {
                        // Keep original widget metrics if rectangle adjustment fails.
                    }
                }

                const textFieldAny = field as any;
                if (typeof textFieldAny.setFontSize === 'function') {
                    textFieldAny.setFontSize(fontSize);
                }
                if (typeof textFieldAny.setAlignment === 'function') {
                    textFieldAny.setAlignment(0);
                }

                field.setText(strVal);
                try {
                    textFieldAny.updateAppearances(helvetica);
                } catch {
                    // Keep flatten resilient even for fields/widgets that reject appearance updates.
                }
            }
        } catch (error) {
            this.log('normalizeFlattenTextFieldAppearance failed', error);
        }
    }

    private async disableAllFormFields() {
        const cache = await this.ensureAcroCache();
        if (!cache || !this.pdfDoc?.annotationStorage) return;

        const storage = this.pdfDoc.annotationStorage;

        for (const name of cache.names) {
            const widgets = cache.objects[name];

            for (const w of widgets || []) {
                storage.setValue(w.id, {
                    readOnly: true,
                    disabled: true
                });
            }
        }

        // 🔥 IMPORTANT: also disable DOM layer
        this.disableFieldsInDOM();

        this.log('All fields disabled (signed mode).');
    }

    private captureAutoPatchedKeys() {
        this.autoPatchedSomKeys.clear();
        this.autoPatchedAcroNames.clear();
        this.autoPatchedWidgetIds.clear();

        for (const [somKey, value] of Object.entries(PATCH_VALUES)) {
            if (this.hasAutoPatchValue(value)) {
                this.autoPatchedSomKeys.add(somKey);
                const acroName = somKey.match(/([^.[]+)\[\d+\]$/)?.[1] ?? somKey;
                this.autoPatchedAcroNames.add(acroName);
            }
        }
    }

    private hasAutoPatchValue(value: any): boolean {
        return value !== undefined && value !== null;
    }

    private async disableAutoPatchedFields() {
        if (!this.autoPatchedSomKeys.size) return;

        const cache = await this.ensureAcroCache();
        if (!cache || !this.pdfDoc?.annotationStorage) return;

        const storage = this.pdfDoc.annotationStorage;
        let widgetsLocked = 0;
        let domNodesLocked = 0;

        for (const somKey of this.autoPatchedSomKeys) {
            const acroName = await this.somToAcroName(somKey);
            const widgets = await this.findWidgetsByName(acroName);

            for (const w of widgets || []) {
                this.autoPatchedWidgetIds.add(String(w.id));
                storage.setValue(w.id, {
                    readOnly: true,
                    disabled: true,
                });
                widgetsLocked++;
            }

            const byName = document.querySelectorAll(
                `input[name="${acroName}"], textarea[name="${acroName}"], select[name="${acroName}"]`
            ) as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

            byName.forEach((el) => {
                el.setAttribute('readonly', 'true');
                el.setAttribute('disabled', 'true');
                (el as HTMLElement).style.pointerEvents = 'none';
                (el as HTMLElement).classList.add('auto-patched-locked-field');
                domNodesLocked++;
            });

            for (const widget of widgets || []) {
                const widgetId = String(widget.id);
                const widgetSelector = `[data-annotation-id="${widgetId}"]`;
                const byWidget = document.querySelectorAll(widgetSelector) as NodeListOf<HTMLElement>;

                byWidget.forEach((host) => {
                    host.style.pointerEvents = 'none';
                    host.classList.add('auto-patched-locked-field');

                    const nested = host.querySelectorAll('input, textarea, select') as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
                    nested.forEach((el) => {
                        el.setAttribute('readonly', 'true');
                        el.setAttribute('disabled', 'true');
                        (el as HTMLElement).style.pointerEvents = 'none';
                        (el as HTMLElement).classList.add('auto-patched-locked-field');
                        domNodesLocked++;
                    });
                });
            }
        }

        this.applyAutoPatchedDomLocks();
        this.bindAutoLockObserver();
        this.log('disableAutoPatchedFields applied', {
            autoSomKeys: this.autoPatchedSomKeys.size,
            autoAcroNames: this.autoPatchedAcroNames.size,
            autoWidgetIds: this.autoPatchedWidgetIds.size,
            widgetsLocked,
            domNodesLocked,
        });
    }

    private applyAutoPatchedDomLocks() {
        if (!this.autoPatchedAcroNames.size && !this.autoPatchedWidgetIds.size) return;

        const container = this.pdfContainer?.nativeElement ?? document;

        for (const acroName of this.autoPatchedAcroNames) {
            const elements = container.querySelectorAll(
                `input[name="${acroName}"], textarea[name="${acroName}"], select[name="${acroName}"]`
            ) as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

            elements.forEach((el) => {
                el.setAttribute('readonly', 'true');
                el.setAttribute('disabled', 'true');
                (el as HTMLElement).style.pointerEvents = 'none';
                (el as HTMLElement).style.userSelect = 'none';
                (el as HTMLElement).classList.add('auto-patched-locked-field');
                if ((el as HTMLInputElement).value !== undefined) {
                    (el as HTMLInputElement).defaultValue = (el as HTMLInputElement).value;
                }
            });
        }

        for (const widgetId of this.autoPatchedWidgetIds) {
            const hosts = container.querySelectorAll(`[data-annotation-id="${widgetId}"]`) as NodeListOf<HTMLElement>;
            hosts.forEach((host) => {
                host.style.pointerEvents = 'none';
                host.style.userSelect = 'none';
                host.classList.add('auto-patched-locked-field');

                const nested = host.querySelectorAll('input, textarea, select') as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
                nested.forEach((el) => {
                    el.setAttribute('readonly', 'true');
                    el.setAttribute('disabled', 'true');
                    (el as HTMLElement).style.pointerEvents = 'none';
                    (el as HTMLElement).classList.add('auto-patched-locked-field');
                });
            });
        }
    }

    private bindAutoLockObserver() {
        const container = this.pdfContainer?.nativeElement;
        if (!container || this.autoLockObserver) return;

        this.autoLockObserver = new MutationObserver(() => {
            this.applyAutoPatchedDomLocks();
        });

        this.autoLockObserver.observe(container, {
            childList: true,
            subtree: true,
        });
    }

    private disableFieldsInDOM() {
        try {
            setTimeout(() => {
                const inputs = document.querySelectorAll(
                    'input, textarea, select'
                ) as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

                inputs.forEach(el => {
                    el.setAttribute('readonly', 'true');
                    el.setAttribute('disabled', 'true');
                    el.style.pointerEvents = 'none';
                    el.style.backgroundColor = '#f5f5f5';
                });
            }, 700); // wait for pdf render
        } catch (error: any) {
            throw Error(error?.message);
        }
    }

    async useTypedSignature() {
        const signControl = this.inputForm.get('signText');
        if (!this.isSignerNameAutoResolved) {
            signControl?.markAsTouched();
            signControl?.updateValueAndValidity();
            if (signControl?.invalid) {
                return;
            }
        }

        const searchString = this.getResolvedSignatureName();
        if (!searchString) {
            return;
        }

        this.placedSignature = {
            type: 'typed',
            data: searchString, // Store raw text here to avoid base64 rendering in HTML
            x: 0,
            y: 0,
            width: DEFAULT_SIGNATURE.BASE_WIDTH,
            height: DEFAULT_SIGNATURE.BASE_HEIGHT,
            baseWidth: DEFAULT_SIGNATURE.BASE_WIDTH,
            baseHeight: DEFAULT_SIGNATURE.BASE_HEIGHT,
            isDragging: false,
            isResizing: false,
            isFixed: true,
            pageIndex: -1,
        };

        try {
            await this.patchSignatureToPdf(true);
            await this.fixSignature();
            // setTimeout(() => {
            //     window.scrollTo({ top: 0, behavior: 'smooth' });
            // }, 100);
            await this.scrollPdfToBottom();
            // FIX: capture pageIndex before patchSignatureToPdf nulls placedSignature;
            // the local variable `pageIndex` is used safely even when placedSignature is null.
            const lastPage = this.pdfDoc?.numPages ?? 1;
            const targetPage = (this.placedSignature?.pageIndex ?? -1) === -1
                ? lastPage - 1
                : (this.placedSignature?.pageIndex ?? 0);
            this.scrollToSignaturePage(targetPage);
        } catch (error) {
            if (this.placedSignature) {
                this.placedSignature.isFixed = false;
                this.placedSignature.pageIndex = 0;
            } else {
                // Re-create a draggable overlay so the user can try again
                this.placeSignatureOnPdf('typed', searchString);
                return;
            }
            this.placeSignatureOnPdf('typed', searchString);
        }
    }

    placeSignatureOnPdf(type: SignatureType, data: string) {
        this.signatureScale = 1;
        this.placedSignature = {
            type,
            data,
            x: 100,
            y: 200,
            width: DEFAULT_SIGNATURE.BASE_WIDTH,
            height: DEFAULT_SIGNATURE.BASE_HEIGHT,
            baseWidth: DEFAULT_SIGNATURE.BASE_WIDTH,
            baseHeight: DEFAULT_SIGNATURE.BASE_HEIGHT,
            isDragging: false,
            isResizing: false,
            isFixed: false,
            pageIndex: 0,
        };

        if (!this.pdfReady || !this.pdfContainer?.nativeElement) {
            setTimeout(() => this.reparentOverlayIfNeeded(), 0);
            return;
        }
        this.attachOverlayToPage(0);
        this.syncOverlayDomSizeFromModel();
        this.fitTypedSignatureFontToBox();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onSignatureMouseDown(event: MouseEvent) {
        if (!this.placedSignature || this.placedSignature.isFixed || !this.signatureOverlay) return;
        const rect = this.signatureOverlay.nativeElement.getBoundingClientRect();
        this.dragOffset.x = event.clientX - rect.left;
        this.dragOffset.y = event.clientY - rect.top;
        this.placedSignature.isDragging = true;
        event.preventDefault();
        event.stopPropagation();
    }

    async onPdfMouseMove(event: MouseEvent) {
        if (!this.placedSignature || !this.placedSignature.isDragging || !this.signatureOverlay) return;

        const overlay = this.signatureOverlay.nativeElement;
        const pageEl = await this.getPageElementAtPoint(event.clientX, event.clientY);
        if (!pageEl) return;

        const newPageIndex = await this.getPageIndex(pageEl);
        if (overlay.parentElement !== pageEl) {
            pageEl.appendChild(overlay);
            this.placedSignature.pageIndex = newPageIndex;
        }

        const { xPx, yPx } = await this.clientToPagePixels(event, pageEl, this.dragOffset);
        this.placedSignature.x = xPx;
        this.placedSignature.y = yPx;

        overlay.style.left = `${xPx}px`;
        overlay.style.top = `${yPx}px`;
        await this.updateSignatureInputState();
    }

    onPdfMouseUp() {
        if (this.placedSignature) {
            this.placedSignature.isDragging = false;
        }
    }

    async fixSignature() {
        if (!this.placedSignature) return;
        this.placedSignature.isFixed = true;
        this.signatureOverlay?.nativeElement.classList.add('fixed');
    }

    cancelPlacement() {
        this.placedSignature = null;
    }

    async removeSignature() {
        if (this.originalPdfBlob) {
            try {
                // 1. Capture current form data before reverting
                const unifiedValues = await this.getUnifiedFormValues();

                // 2. Load clean original PDF
                const pdfData = await this.originalPdfBlob.arrayBuffer();
                const pdfDoc = await PDFDocument.load(pdfData);
                const form = pdfDoc.getForm();

                // 3. Re-apply the form data to the clean PDF
                for (const [key, val] of Object.entries(unifiedValues)) {
                    // Skip the signature field itself and any suspected corrupted data
                    if (key === 'signature' || String(val).startsWith('data:image') || String(val).length > 500) continue;

                    try {
                        const acroName = await this.somToAcroName(key);
                        const field = form.getField(acroName);
                        if (field instanceof PDFTextField) {
                            const safeValue = await this.sanitizePdfValue(val);
                            field.setText(safeValue);
                        } else if (field instanceof PDFCheckBox) {
                            if (await this.isCheckboxChecked(val)) field.check();
                            else field.uncheck();
                        }
                    } catch (e) { }
                }

                // 4. Save and update viewer
                const finalBytes = await pdfDoc.save();
                const newPdfBlob = new Blob([finalBytes as any], { type: 'application/pdf' });
                this.pdfSource = window.URL.createObjectURL(newPdfBlob);
            } catch (err) {
                // Fallback to original blob if patching fails
                this.pdfSource = window.URL.createObjectURL(this.originalPdfBlob);
            }
        }
        this.placedSignature = null;
        this.isSignatureFixed = false;
    }

    async indexFormFields() {
        if (!this.pdfDoc?.getFieldObjects) return;

        const fieldObjects = await this.pdfDoc.getFieldObjects();

        if (!fieldObjects || !Object.entries(fieldObjects)) return;
        else {
            Object.entries(fieldObjects)?.forEach(([name, arr]: any) => {
                arr.forEach((f: any) => {
                    if (f?.id) this.fieldIndexById.set(String(f.id), { name, type: f.type });
                });
            });
        }
    }

    /** SOM path "topmostSubform[0]...f1_1[0]" → probable Acro field name "f1_1" */
    private async somToAcroName(som: string) {
        const m = som.match(/([^.[]]+)\[\d+\]$/);
        return m ? m[1] : som; // fallback: raw tail
    }

    private async ensureAcroCache() {
        if (!this.pdfDoc?.getFieldObjects) return null;
        if (!this.acroCache) {
            const objects = await this.pdfDoc.getFieldObjects();
            if (objects && Object.keys(objects))
                this.acroCache = { objects, names: Object.keys(objects) };

        }
        return this.acroCache;
    }

    private async findWidgetsByName(candidate: string) {
        if (!this.acroCache) return null;
        const { objects, names } = this.acroCache;
        if (objects[candidate]) return objects[candidate];
        const ends = names.find((n) => n.endsWith(candidate));
        if (ends) return objects[ends];
        const contains = names.find((n) => n.includes(candidate));
        if (contains) return objects[contains];
        return null;
    }

    /** Apply Acro patch to annotationStorage (no download yet) */
    private async applyAcroPatch(somValues: Record<string, any>) {
        const cache = await this.ensureAcroCache();
        if (!cache || !this.pdfDoc?.annotationStorage) return;

        const storage = this.pdfDoc.annotationStorage;

        let patched = 0;
        for (const [somKey, somVal] of Object.entries(somValues)) {
            const name = await this.somToAcroName(somKey);
            const widgets = await this.findWidgetsByName(name);
            if (!widgets?.length) {
                this.log('applyAcroPatch → no widgets for', name, 'from SOM', somKey);
                continue;
            }

            this.log('applyAcroPatch mapping', { somKey, derivedAcroName: name, widgetCount: widgets.length, value: somVal });

            for (const w of widgets) {
                if (w.type === 'text' || w.type === 'combobox' || w.type === 'textarea') {
                    storage.setValue(w.id, { value: String(somVal ?? '') });
                    patched++;
                } else if (w.type === 'checkbox') {
                    const onValue = w.exportValues || 'On';
                    storage.setValue(w.id, { value: !!somVal ? onValue : 'Off' });
                    patched++;
                } else if (w.type === 'radio') {
                    if (!!somVal) {
                        const onValue = w.exportValues || 'On';
                        storage.setValue(w.id, { value: onValue });
                        patched++;
                    }
                }
            }
        }

        this.log('applyAcroPatch completed', { patched, attempted: Object.keys(somValues).length });
    }

    /** Patch and download (fields only) */
    async patchAcroAndDownload(somValues: Record<string, any>, fileName = 'form-patched.pdf') {
        if (!this.pdfDoc) return;
        await this.applyAcroPatch(somValues);
    }

    async patch() {
        if (!this.pdfReady || !this.pdfDoc) {
            return;
        }

        await this.patchAfterLoad();
    }

    async downloadSignedPdfWithSignature(skipValidation: boolean = false) {
        try {
            if (!skipValidation) {
                // 🔐 REQUIRED FIELD VALIDATION (BLOCK SIGNING)
                if (!await this.guardRequiredFieldsOrAlert()) {
                    return;
                }
            }

            if (this.docData?.isSigned || this.isSignatureFixed) {
                await this.disableAllFormFields();
            }

            if (!this.pdfDoc) {
                return;
            }
            if (!this.isSignatureFixed) {
                return;
            }
            // Ensure overlay reflects any latest scale/typed font before measuring
            if (this.placedSignature?.pageIndex !== -1) {
                this.syncOverlayDomSizeFromModel();
                this.fitTypedSignatureFontToBox();
            }
            // 1) Patch fields
            await this.applyAcroPatch(PATCH_VALUES);
            return await this.patchSignatureToPdf(true, true);
        } catch (error: any) {
            throw Error(error?.message)
        }
    }

    private async patchSignatureToPdf(isAutoPatch = false, returnFile = false) {
        if (!this.placedSignature && !returnFile) return;

        this.captureCurrentDomValues();

        // 1. Get current PDF data - ALWAYS start from original to prevent cumulative artifacts
        let pdfData: ArrayBuffer | Uint8Array;
        if (this.originalPdfBlob) {
            pdfData = await this.originalPdfBlob.arrayBuffer();
        } else if (this.pdfSource && typeof this.pdfSource === 'string' && this.pdfSource.startsWith('blob:')) {
            const response = await fetch(this.pdfSource);
            pdfData = await response.arrayBuffer();
        } else {
            // Fallback
            const bytes: Uint8Array = await this.pdfDoc.saveDocument();
            pdfData = bytes;
        }

        // 2. Load into pdf-lib
        const pdfDoc = await PDFDocument.load(pdfData);

        // --- NEW: Combined patching logic ---
        const unifiedValues = await this.getUnifiedFormValues();
        const form = pdfDoc.getForm();

        // 1. First, sync all interactive form fields to prevent data loss
        for (const [key, val] of Object.entries(unifiedValues)) {
            try {
                if (key === 'signature' || String(val).startsWith('data:image')) continue;

                const acroName = await this.somToAcroName(key);
                const field = form.getField(acroName);
                if (field instanceof PDFTextField) {
                    // Filter out suspiciously long strings (likely corrupted data or base64)
                    const safeValue = await this.sanitizePdfValue(val);
                    field.setText(safeValue);
                } else if (field instanceof PDFCheckBox) {
                    if (await this.isCheckboxChecked(val)) field.check();
                    else field.uncheck();
                }
            } catch (e) { }
        }

        // 2. Then, apply Coordinate-based patching for all schema fields
        const schema = PDF_SCHEMAS[this.documentTitle];
        const sigConfig = schema?.find(f => f.key === 'signature');
        const placedPageIndex = this.placedSignature?.pageIndex ?? -1;

        // Resolve signature page first so other drawn fields (like date) can follow it.
        let signatureTargetPageIndex = placedPageIndex;
        if (isAutoPatch && sigConfig && placedPageIndex === -1) {
            signatureTargetPageIndex = sigConfig.ui.page - 1;
        }
        if (signatureTargetPageIndex === -1) {
            signatureTargetPageIndex =
                this.documentTitle !== DOCUMENT_TITLES.ParticipatingAgreement
                    ? 0
                    : pdfDoc.getPageCount() - 1;
        }

        const now = new Date();
        const dateStr = `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${now.getFullYear()}`;

        if (schema) {
            for (const fieldConfig of schema) {
                if (fieldConfig.key === 'signature') continue;

                const val = fieldConfig.type === 'date'
                    ? dateStr
                    : (unifiedValues[fieldConfig.key] ?? unifiedValues[await this.somToAcroName(fieldConfig.key)]);

                if (val !== undefined && val !== null && val !== '') {
                    try {
                        const strVal = String(val);
                        if (strVal.length > 500) continue; // Skip suspected corrupted data

                        let pageIdx = (fieldConfig.ui.page || 1) - 1;

                        // Participating Agreement date field moved; keep it on signature page.
                        if (this.documentTitle === DOCUMENT_TITLES.ParticipatingAgreement && fieldConfig.type === 'date') {
                            pageIdx = signatureTargetPageIndex;
                        }

                        if (pageIdx < pdfDoc.getPageCount()) {
                            const p = pdfDoc.getPage(pageIdx);
                            const { height: ph } = p.getSize();

                            p.drawText(strVal, {
                                x: fieldConfig.ui.x,
                                y: ph - fieldConfig.ui.y - (fieldConfig.ui.height / 2) - 5,
                                size: 11,
                                color: rgb(0, 0, 0),
                            });
                        }
                    } catch (err) { }
                }
            }
        }

        let pageIndex = signatureTargetPageIndex;

        const page = pdfDoc.getPage(pageIndex);
        const { width: pdfWidth, height: pdfHeight } = page.getSize();

        let x, y, drawWidth, drawHeight;

        if (isAutoPatch) {
            if (sigConfig && placedPageIndex === -1) {
                // FIXED: Schema-based placement
                pageIndex = signatureTargetPageIndex;
                drawWidth = sigConfig.ui.width;
                drawHeight = sigConfig.ui.height;
                x = sigConfig.ui.x;
                y = pdfHeight - sigConfig.ui.y - drawHeight;

                this.log('Schema-based placement:', { pageIndex, x, y, drawWidth, drawHeight });
            } else {
                // FALLBACK: Initialize variables before they are used in calculations
                drawWidth = this.placedSignature?.width || DEFAULT_SIGNATURE.BASE_WIDTH;
                drawHeight = this.placedSignature?.height || DEFAULT_SIGNATURE.BASE_HEIGHT;

                if (this.placedSignature && (this.placedSignature?.pageIndex || -1) === -1) {
                    const { alignment, marginX, marginY } = this.autoPlacementConfig;

                    switch (alignment) {
                        case 'bottom-right':
                            x = pdfWidth - drawWidth - marginX;
                            y = marginY;
                            break;
                        case 'bottom-left':
                            x = marginX;
                            y = marginY;
                            break;
                        case 'top-right':
                            x = pdfWidth - drawWidth - marginX;
                            y = pdfHeight - drawHeight - marginY;
                            break;
                        case 'top-left':
                            x = marginX;
                            y = pdfHeight - drawHeight - marginY;
                            break;
                        default:
                            x = pdfWidth - drawWidth - marginX;
                            y = marginY;
                    }
                } else {
                    // Manual placement was triggered with autoPatch=true (e.g. drawn signature)
                    x = this.placedSignature?.x || 0;
                    y = pdfHeight - (this.placedSignature?.y || 0) - (this.placedSignature?.height || DEFAULT_SIGNATURE.BASE_HEIGHT);
                }
            }
        } else {

            // Manual placement logic from the new implementation
            const pageEl = this.getPageElByIndex(pageIndex);
            if (!pageEl) throw new Error('Page element not found');

            const contentRect = this.measureSignatureContentRect(pageEl);
            if (!contentRect) throw new Error('Content rect not found');

            const { leftCss, topCss, widthCss, heightCss, pageRect } = contentRect;
            const scaleX = pdfWidth / pageRect.width;
            const scaleY = pdfHeight / pageRect.height;

            x = leftCss * scaleX;
            y = pdfHeight - (topCss * scaleY) - (heightCss * scaleY);
            drawWidth = widthCss * scaleX;
            drawHeight = heightCss * scaleY;
        }

        this.sigData = this.placedSignature?.data || this.sigData || '';

        const signatureUiConfig: any = PDF_SCHEMAS[this.documentTitle]?.find(f => f.key === 'signature');
        if (this.placedSignature?.type === 'typed') {
            if (!signatureUiConfig) {
                return;
            }

            const MAX_W = Math.max(1, signatureUiConfig.ui.width);   // avoid 0
            const MAX_H = Math.max(1, signatureUiConfig.ui.height);  // avoid 0
            const PADDING_X = 2;  // left/right padding inside the signature box
            const PADDING_Y = 2;   // top/bottom padding inside the signature box

            const canvas = document.createElement('canvas');
            // We render at higher res for crisp downscaled result in PDF
            const SCALE = 2; // 2x makes signatures look sharper in PDFs
            canvas.width = Math.floor(MAX_W * SCALE);
            canvas.height = Math.floor(MAX_H * SCALE);

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            let text = (this.sigData || '').toString().trim();
            text = this.getResolvedSignatureName();
            // Guard: if user pasted data URL by mistake, strip it
            if (text.startsWith('data:image')) {
                text = '';
            }
            
            // Font settings
            const MIN_FONT = 10 * SCALE;
            let fontSize = 26 * SCALE;

            if (text.length <= 10) fontSize = 20 * SCALE;
            else if (text.length <= 20) fontSize = 17 * SCALE;
            else if (text.length <= 25) fontSize = 15 * SCALE;
            else if (text.length <= 50) fontSize = 14 * SCALE;
            else if (text.length <= 60) fontSize = 13 * SCALE;
            else if (text.length <= 75) fontSize = 12 * SCALE;
            else fontSize = 9 * SCALE;

            const fontFamily = `"Brush Script MT","Segoe Script","Lucida Handwriting",cursive`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.fillStyle = '#000';

            // Helper to set font and measure width/height
            const setFont = (fs: number) => { ctx.font = `${fs}px ${fontFamily}`; };

            const measure = (txt: string) => {
                const m = ctx.measureText(txt);
                // Prefer modern metrics if available; otherwise fall back
                const ascent = (m.actualBoundingBoxAscent ?? 0);
                const descent = (m.actualBoundingBoxDescent ?? Math.max(0, m.fontBoundingBoxDescent ?? 0));
                let textH = ascent + descent;
                // Fallback if both are 0 (older browsers)
                if (textH === 0) {
                    // Approximate with 1.2 * fontSize (typical line-height factor)
                    textH = fontSize * 1.2;
                }
                return { width: m.width, height: textH, ascent, descent };
            };

            setFont(fontSize);
            let { width: w, height: h } = measure(text);

            // Available drawing area after padding
            const boxW = canvas.width - PADDING_X * 2 * SCALE;
            const boxH = canvas.height - PADDING_Y * 2 * SCALE;

            // Shrink loop: ensure both width and height fit
            while ((w > boxW || h > boxH) && fontSize > MIN_FONT) {
                fontSize = Math.max(MIN_FONT, fontSize - 1 * SCALE);
                setFont(fontSize);
                const m = measure(text);
                w = m.width;
                h = m.height;
            }

            // Clear and draw centered
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Compute center position: x is horizontal center; y is vertical center using ascent
            //const centerX = canvas.width / 2;
            const startX = PADDING_X * SCALE;
            const metrics = measure(text);
            // Place baseline so the glyph area is vertically centered
            //const yBaseline = Math.round(canvas.height / 2 + h / 2 - (measure(text).descent || 0));
            const yBaseline = (canvas.height - metrics.height) / 2 + metrics.ascent;
            setFont(fontSize);
            ctx.fillText(text, startX, yBaseline);

            // Convert to PNG at native resolution for crisp embedding
            this.sigData = canvas.toDataURL('image/png');
        }

        const signatureImage = await pdfDoc.embedPng(this.sigData);

        // 6. Draw on page
        page.drawImage(signatureImage, {
            x,
            y,
            width: signatureUiConfig?.ui.width ?? drawWidth,   // prefer schema width
            height: signatureUiConfig?.ui.height ?? drawHeight // prefer schema height
        });

        if (this.docData?.isSigned || this.isSignatureFixed) {
            try {
                const form = pdfDoc.getForm();
                await this.normalizeFlattenTextFieldAppearance(pdfDoc, unifiedValues);
                form.flatten();
            } catch (e) {
            }
        }

        // 7. Save
        const finalBytes = await pdfDoc.save();
        const pdfBlob = new Blob([finalBytes as any], { type: 'application/pdf' });
        const fileName = `${this.documentTitle || 'signed'}-${Date.now()}.pdf`;

        const finalFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

        if (!returnFile) {
            this.pdfSource = URL.createObjectURL(pdfBlob);
            await this.waitForPdfReady();
            await this.updateSignatureInputState();
            this.isSignatureFixed = true;
            this.placedSignature = null;
        }

        return finalFile;
    }

    private async sanitizePdfValue(val: any): Promise<string> {
        if (val === null || val === undefined) return '';

        // if already string
        if (typeof val === 'string') return val;

        // numbers
        if (typeof val === 'number') return String(val);

        // boolean
        if (typeof val === 'boolean') return val ? 'Yes' : '';

        // if object with value property
        if (typeof val === 'object') {
            if ('value' in val && typeof val.value !== 'object') {
                return String(val.value ?? '');
            }
            return '';
        }

        return '';
    }

    private attachOverlayToPage(pageIndex: number) {
        if (!this.signatureOverlay || !this.placedSignature) return;
        const pageEl = this.getPageElByIndex(pageIndex);
        if (!pageEl) {
            requestAnimationFrame(() => this.attachOverlayToPage(pageIndex));
            return;
        }
        pageEl.appendChild(this.signatureOverlay.nativeElement);
        this.signatureOverlay.nativeElement.style.left = `${this.placedSignature.x}px`;
        this.signatureOverlay.nativeElement.style.top = `${this.placedSignature.y}px`;
        this.syncOverlayDomSizeFromModel();
        this.fitTypedSignatureFontToBox();
        this.placedSignature.pageIndex = pageIndex;
    }

    private reparentOverlayIfNeeded() {
        if (!this.placedSignature || !this.signatureOverlay) return;
        const idx = this.placedSignature.pageIndex ?? 0;
        this.attachOverlayToPage(idx);
    }

    private async getPageElementAtPoint(clientX: number, clientY: number) {
        const el = document.elementFromPoint(clientX, clientY);
        return el ? (el as HTMLElement).closest('.page') as HTMLElement : null;
    }

    private async getPageIndex(pageEl: HTMLElement): Promise<number> {
        const numAttr = pageEl.getAttribute('data-page-number');
        return numAttr ? parseInt(numAttr, 10) - 1 : 0;
    }

    private getPageElByIndex(pageIndex: number): HTMLElement | null {
        if (!this.pdfContainer || !this.pdfContainer.nativeElement) return null;
        return this.pdfContainer.nativeElement.querySelector(
            `.page[data-page-number="${pageIndex + 1}"]`
        );
    }

    private async clientToPagePixels(
        evt: MouseEvent,
        pageEl: HTMLElement,
        dragOffset?: { x: number; y: number }
    ) {
        const rect = await pageEl.getBoundingClientRect();
        const xClient = evt.clientX - rect.left;
        const yClient = evt.clientY - rect.top;
        return {
            xPx: dragOffset ? xClient - dragOffset.x : xClient,
            yPx: dragOffset ? yClient - dragOffset.y : yClient,
        };
    }

    /** Ensure overlay DOM width/height match the model */
    private syncOverlayDomSizeFromModel() {
        if (!this.signatureOverlay?.nativeElement || !this.placedSignature) return;
        const el = this.signatureOverlay.nativeElement;
        el.style.width = `${this.placedSignature.width}px`;
        el.style.height = `${this.placedSignature.height}px`;
    }

    /** Inner content element (img for uploaded/drawn or typed text div) */
    private getSignatureContentElement(): HTMLElement | null {
        const overlay = this.signatureOverlay?.nativeElement;
        if (!overlay) return null;
        const img = overlay.querySelector('img') as HTMLElement | null;
        if (img) return img;
        const typed = overlay.querySelector('.typed-signature') as HTMLElement | null;
        if (typed) return typed;
        return overlay; // fallback: whole overlay
    }

    /** Measure content rect relative to the page element (CSS px) */
    private measureSignatureContentRect(pageEl: HTMLElement) {
        const contentEl = this.getSignatureContentElement();
        const pageRect = pageEl.getBoundingClientRect();
        const targetRect =
            (contentEl ?? this.signatureOverlay?.nativeElement)?.getBoundingClientRect();
        if (!targetRect) return null;

        const leftCss = targetRect.left - pageRect.left;
        const topCss = targetRect.top - pageRect.top;
        const widthCss = targetRect.width;
        const heightCss = targetRect.height;

        return { leftCss, topCss, widthCss, heightCss, pageRect };
    }

    /** Fit typed signature font-size to overlay box (width & height) */
    private fitTypedSignatureFontToBox() {
        if (!this.placedSignature || this.placedSignature.type !== 'typed') return;
        const typedEl = this.signatureOverlay?.nativeElement.querySelector(
            '.typed-signature'
        ) as HTMLElement | null;
        if (!typedEl) return;

        const targetWidth = this.placedSignature.width;
        const targetHeight = this.placedSignature.height;
        const text = this.placedSignature.data || '';

        const maxSizeByHeight = Math.max(8, targetHeight);
        let low = 8,
            high = maxSizeByHeight,
            best = low;

        for (let i = 0; i < 12; i++) {
            const mid = (low + high) / 2;
            const w = this.measureTypedTextWidthAtSize(text, mid, typedEl);
            if (w <= targetWidth) {
                best = mid;
                low = mid;
            } else {
                high = mid;
            }
        }

        const finalSize = Math.floor(Math.min(best, maxSizeByHeight));
        typedEl.style.fontSize = `${finalSize}px`;
        typedEl.style.lineHeight = '1';
        typedEl.style.whiteSpace = 'nowrap';
        typedEl.style.display = 'flex';
        typedEl.style.width = '100%';
        typedEl.style.height = '100%';
        typedEl.style.alignItems = 'center';
        typedEl.style.justifyContent = 'flex-start';
        typedEl.style.paddingLeft = '8px';
    }

    /** Width of text at font-size using DOM font (offscreen canvas) */
    private measureTypedTextWidthAtSize(text: string, fontSizePx: number, elem: HTMLElement): number {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return text.length * fontSizePx * 0.5;

        const cs = getComputedStyle(elem);
        const fontStyle = cs.fontStyle || 'normal';
        const fontWeight = cs.fontWeight || 'normal';
        const fontFamily = cs.fontFamily || 'BrushScript';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSizePx}px ${fontFamily}`;
        const m = ctx.measureText(text || '');
        return m.width;
    }

    private getResolvedSignatureName(): string {
        if (this.isSignerNameAutoResolved) {
            return this.expectedSignerName.trim();
        }

        return (this.inputForm.get('signText')?.value || '').toString().trim();
    }

    private resolveExpectedProviderSignatureName(): string {
        const demographics = JSON.parse(localStorage.getItem('demographics') || '{}');
        const loggedUserDetails = JSON.parse(localStorage.getItem('loggedUserDetails') || '{}');
        const demographicLastName = demographics?.lastName || '';
        const demographicFullName = `${demographics?.firstName || ''} ${demographicLastName}`.trim();

        if (demographicFullName) {
            return demographicFullName.slice(0, SIGNATURE_MAX_LENGTH);
        }

        return `${loggedUserDetails?.firstName || ''} ${loggedUserDetails?.lastName || ''}`
            .trim()
            .slice(0, SIGNATURE_MAX_LENGTH);
    }

    async uploadSignedPdf() {
        (document.activeElement as HTMLElement)?.blur();
        await new Promise(resolve => setTimeout(resolve, 200));

        if (!this.isSignatureFixed) {
            alert('Please place and fix the signature first');
            return;
        }
        try {
            this.captureCurrentDomValues();

            const guardPassed = await this.guardRequiredFieldsOrAlert();
            if (!guardPassed) {
                return;
            }

            await this.saveDocument(null, true /* skipValidation */);
            this.goBack(true);
        } catch (error) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.log("Error while saving document");
        }
    }

    goBack(signed: boolean = false) {
        this.router.navigate(['/credentialing/sign-documents'], {
            queryParams: signed ? { signed: 'true' } : {}
        });
    }

    private async guardRequiredFieldsOrAlert(): Promise<any> {
        try {
            if (this.documentTitle.toLocaleLowerCase() == 'uniformrequirements') return true;

            await this.clearHighlights();
            const requiredResult = await this.validateRequiredFields();

            if (!requiredResult.valid) {
                const alertMessage: string[] = requiredResult.missing.map(f => f);
                const dialogRef = this.dialog.open(PopupComponent, {
                    disableClose: true,
                    data: {
                        headerMessage: 'Please fill the following required fields',
                        message: alertMessage, isPTESaved: true
                    },
                    width: '550px',
                    backdropClass: '',
                    hasBackdrop: true,
                });
                dialogRef.afterClosed().subscribe(result => {
                    if (result === true) {
                        return;
                    }
                });
                await this.highlightMissingFields(requiredResult.missingKeys);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return false;
            }

            // 🔎 Pattern validation
            const unifiedValues = await this.getUnifiedFormValues();
            const patternResult = await this.validatePatternFields(unifiedValues);
            console.log('Pattern validation result:', patternResult);
            if (!patternResult.valid) {
                const alertMessage = patternResult.errors.map(e => `• ${e} `).join('\n');
                const dialogRef = this.dialog.open(PopupComponent, {
                    disableClose: true,
                    data: {
                        headerMessage: 'Please correct the following field formats',
                        message: alertMessage, isPTESaved: true
                    },
                    width: '550px',
                    backdropClass: '',
                    hasBackdrop: true,
                });
                dialogRef.afterClosed().subscribe(result => {
                    if (result === true) {
                        return;
                    }
                });
                return false;
            }

            return true;
        } catch (error: any) {
            throw Error(error?.message);
        }
    }

    private async validatePatternFields(values: Record<string, any>) {
        const errors: string[] = [];

        for (const [somKey, cfg] of Object.entries(FIELD_VALIDATIONS)) {
            const acroName: any = await this.somToAcroName(somKey);
            const val =
                values[somKey] ??
                values[acroName];

            if (val && !cfg.pattern.test(String(val).trim())) {
                errors.push(`${cfg.label} has invalid format`);
            }
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }

    private async clearHighlights() {
        try {
            const highlighted = document.querySelectorAll('.missing-field-highlight');
            highlighted.forEach(el => el.classList.remove('missing-field-highlight'));
        } catch (error: any) {
            throw Error(error?.message)
        }
    }

    private async highlightMissingFields(missingSomKeys: string[], scrollToFirst: boolean = true) {
        try {
            let firstElementScrolled = false;

            for (const somKey of missingSomKeys) {
                const acroName = await this.somToAcroName(somKey);
                const widgets = await this.findWidgetsByName(acroName);
                let found = false;

                for (const w of widgets || []) {
                    // Strategy 1: Find by Name
                    const explicitSelector = `input[name="${acroName}"], textarea[name="${acroName}"], select[name="${acroName}"]`;
                    let elements: NodeListOf<Element> | HTMLElement[] = document.querySelectorAll(explicitSelector);

                    // If you have DOM mapping, add a CSS class here
                    if (elements.length > 0) {
                        found = true;
                    } else {
                        const widgets = await this.findWidgetsByName(acroName);
                        if (widgets && widgets.length > 0) {
                            const selectors = widgets.flatMap(w => [
                                `[data-annotation-id="${w.id}"]`,
                                `[data-annotation-id="${w.id}"] input`,
                                `[data-annotation-id="${w.id}"] textarea`,
                                `[data-annotation-id="${w.id}"] select`,
                                `[id="${w.id}"]`
                            ]);
                            const combinedSelector = selectors.join(', ');
                            try {
                                elements = document.querySelectorAll(combinedSelector);
                                if (elements.length > 0) {
                                    found = true;
                                }
                            } catch (error) {
                                console.error('Invalid selector generated', combinedSelector, error);
                            }
                        }
                    }

                    if (elements.length > 0) {
                        elements.forEach((el) => {
                            const element = el as HTMLElement;
                            element.classList.add('missing-field-highlight');

                            if (!firstElementScrolled) {
                                if (scrollToFirst) {
                                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                                firstElementScrolled = true;
                            }
                        });
                    } else {
                        this.log('Could not find DOM element for field:', acroName);
                    }
                }
            }
        } catch (error: any) {
            throw Error(error?.message);
        }
    }

    private async getUnifiedFormValues() {
        const result: Record<string, any> = {};

        // Track fields intentionally cleared by the user so fallback sources don't re-populate them.
        const explicitlyCleared = new Set<string>();

        // 1️⃣ Merge annotationStorage (PDF.js in-memory store for the current viewer instance)
        const storageAll =
            this.pdfDoc?.annotationStorage?.getAll?.() ?? {};

        for (const [id, payload] of Object.entries(storageAll)) {
            const idx = this.fieldIndexById.get(id);
            if (!idx) continue;

            const val =
                (payload as any)?.value ??
                (payload as any)?.textContent ??
                (payload as any)?.checked ??
                payload;

            // IMPORTANT: Do NOT filter out 'Off' here — let checkbox logic handle it
            if (val !== undefined && val !== null) {
                result[idx.name] = val;
                if (typeof val === 'string' && val.trim() === '') {
                    explicitlyCleared.add(idx.name);
                }
            }
        }

        // 2️⃣ DOM values override storage for currently-rendered PDF inputs.
        const allKnownAcroNames = Array.from(
            new Set(
                Array.from(this.fieldIndexById.values()).map(v => v.name)
            )
        );

        for (const acroName of allKnownAcroNames) {

            // value already captured from annotationStorage
            if (
                result[acroName] !== undefined &&
                result[acroName] !== null &&
                result[acroName] !== ''
            ) {
                continue;
            }

            const input = document.querySelector(
                `input[name="${acroName}"], textarea[name="${acroName}"], select[name="${acroName}"]`
            ) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;

            if (!input) {
                const somKeyMatch = Object.keys(PATCH_VALUES).find(
                    k => k === acroName || k.endsWith(`.${acroName}[0]`) || k.endsWith(`.${acroName}`) || k === `${acroName}[0]`
                );
                if (somKeyMatch && PATCH_VALUES[somKeyMatch] !== undefined && PATCH_VALUES[somKeyMatch] !== null) {
                    result[acroName] = PATCH_VALUES[somKeyMatch];
                }
                continue;
            }

            if (input instanceof HTMLInputElement && (input.type === 'checkbox' || input.type === 'radio')) {
                result[acroName] = input.checked;
            } else {
                result[acroName] = input.value?.trim() || '';
                if (!result[acroName]) {
                    explicitlyCleared.add(acroName);
                } else {
                    explicitlyCleared.delete(acroName);
                }
            }
        }

        // 3️⃣ Fallback to PATCH_VALUES only when value is truly missing (not explicitly cleared).
        for (const [key, value] of Object.entries(PATCH_VALUES)) {
            const existing = result[key];
            const hasExisting =
                existing !== undefined &&
                existing !== null &&
                (!(typeof existing === 'string') || existing.trim() !== '');

            if (!hasExisting && !explicitlyCleared.has(key)) {
                result[key] = value;
            }
        }

        // 4️⃣ Include values already present in the loaded PDF (prefilled/offscreen), unless user cleared them.
        const fieldObjects = await this.pdfDoc?.getFieldObjects?.();
        if (fieldObjects) {
            for (const [acroName, widgets] of Object.entries(fieldObjects as Record<string, any[]>)) {
                const existing = result[acroName];
                const hasExisting =
                    existing !== undefined &&
                    existing !== null &&
                    (!(typeof existing === 'string') || existing.trim() !== '');

                if (hasExisting || explicitlyCleared.has(acroName)) continue;

                const matchedWidget = (widgets || []).find((w: any) => {
                    const v = w?.value ?? w?.fieldValue ?? w?.defaultValue;
                    if (v === undefined || v === null) return false;
                    if (typeof v === 'string') return v.trim() !== '';
                    return true;
                });

                if (matchedWidget) {
                    result[acroName] =
                        matchedWidget.value ??
                        matchedWidget.fieldValue ??
                        matchedWidget.defaultValue;
                }
            }
        }

        const allRequiredKeys = this.documentTitle === DOCUMENT_TITLES.ParticipatingAgreement
            ? PA_REQUIRED_FIELDS.map(f => f.key)
            : [
                ...W9_ALWAYS_REQUIRED_FIELDS.map(f => f.key),
                ...W9_SSN_KEYS,
                ...W9_EIN_KEYS,
              ];

        for (const somKey of allRequiredKeys) {
            // Already resolved
            if (result[somKey] !== undefined && result[somKey] !== null && result[somKey] !== '') continue;

            const acroName = somKey.match(/([^.[]+)\[\d+\]$/)?.[1] ?? somKey;

            // Respect explicit clear state and avoid auto re-populating required fields.
            if (explicitlyCleared.has(somKey) || explicitlyCleared.has(acroName)) continue;

            // Already resolved under acro name
            if (result[acroName] !== undefined && result[acroName] !== null && result[acroName] !== '') {
                result[somKey] = result[acroName];
                continue;
            }

            // Look up directly in PATCH_VALUES
            if (PATCH_VALUES[somKey] !== undefined && PATCH_VALUES[somKey] !== null) {
                result[somKey] = PATCH_VALUES[somKey];
            } else if (PATCH_VALUES[acroName] !== undefined && PATCH_VALUES[acroName] !== null) {
                result[somKey] = PATCH_VALUES[acroName];
                result[acroName] = PATCH_VALUES[acroName];
            }
        }

        return result;
    }

    private getRelevantFieldKeys(): string[] {
        if (this.documentTitle === DOCUMENT_TITLES.ParticipatingAgreement) {
            return PA_REQUIRED_FIELDS.map(f => f.key);
        }

        // W9 — combine everything
        return [
            ...W9_ALWAYS_REQUIRED_FIELDS.map(f => f.key),
            ...W9_FEDERAL_CLASSIFICATION_KEYS,
            ...W9_SSN_KEYS,
            ...W9_EIN_KEYS,
        ];
    }

    private async validateRequiredFields() {
        const missing: string[] = [];
        const missingKeys: string[] = [];

        if (!this.pdfDoc) {
            return { valid: false, missing: ['PDF not loaded'], missingKeys: [] };
        }

        const values = await this.getUnifiedFormValues();

        const fieldsMap = this.documentTitle == DOCUMENT_TITLES.ParticipatingAgreement ? PA_REQUIRED_FIELDS : W9_ALWAYS_REQUIRED_FIELDS;

        for (const req of fieldsMap) {
            const val =
                values[req.key] ??
                values[await this.somToAcroName(req.key)];

            const hasValue =
                val !== undefined &&
                val !== null &&
                (!(typeof val === 'string') || val.trim());

            if (!hasValue) {
                missing.push(req.label);
                missingKeys.push(req.key);
            }
        }

        if (this.documentTitle != DOCUMENT_TITLES.ParticipatingAgreement) {
            // 2️⃣ Federal Classification — at least one

            let hasFederalClassification = false;

            for (const k of W9_FEDERAL_CLASSIFICATION_KEYS) {
                const v = values[k] ?? values[await this.somToAcroName(k)];

                if (await this.isCheckboxChecked(v)) {
                    hasFederalClassification = true;
                    break;
                }
            }

            if (!hasFederalClassification) {
                missing.push('Federal Classification (select one)');
                missingKeys.push(...W9_FEDERAL_CLASSIFICATION_KEYS);
            }

            // 3️⃣ SSN or EIN — either full group

            let hasFullSSN = true;

            for (const k of W9_SSN_KEYS) {
                const v = values[k] ?? values[await this.somToAcroName(k)];

                if (!v || !String(v).trim()) {
                    hasFullSSN = false;
                    break;
                }
            }

            let hasFullEIN = true;

            for (const k of W9_EIN_KEYS) {
                const v = values[k] ?? values[await this.somToAcroName(k)];

                if (!v || !String(v).trim()) {
                    hasFullEIN = false;
                    break;
                }
            }


            if (!hasFullSSN && !hasFullEIN) {
                missing.push('Either full SSN or full EIN is required');
                missingKeys.push(...W9_SSN_KEYS, ...W9_EIN_KEYS);
            }
        }

        return {
            valid: missing.length === 0,
            missing,
            missingKeys
        };
    }

    private async isCheckboxChecked(v: any): Promise<boolean> {
        if (v === true) return true;
        if (typeof v === 'string') {
            const normalized = v.toLowerCase();
            return normalized === 'yes' || normalized === 'on' || normalized === '1' || normalized === 'true';
        }
        return false;
    }

    validateAllFormFields(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(field => {
            const control = formGroup.get(field);
            if (control instanceof FormGroup) {
                this.validateAllFormFields(control);
            } else {
                control?.markAsTouched({ onlySelf: true });
            }
        });
    }

    private async hardDisableApprovedFieldsInDom() {
        const fields = ['Approved by', 'Approved by Date'];

        setTimeout(() => {
            fields.forEach(label => {
                const inputs = Array.from(document.querySelectorAll('input, textarea'));

                inputs.forEach((el: any) => {
                    if (el.name && el.name.includes('dhFormfield-61230815')) {
                        el.setAttribute('readonly', 'true');
                        el.setAttribute('disabled', 'true');

                        const rect = el.getBoundingClientRect();
                        const blocker = document.createElement('div');
                        blocker.className = 'pdf-readonly-blocker';
                        blocker.style.left = rect.left + 'px';
                        blocker.style.top = rect.top + 'px';
                        blocker.style.width = rect.width + 'px';
                        blocker.style.height = rect.height + 'px';

                        document.body.appendChild(blocker);
                    }
                });
            });
        }, 500);
    }

    async termsCheckboxCheck(event: any, docData: any) {
        if (!this.isPdfRead) {
            event.preventDefault();
            event.stopPropagation();
            this.showErrorMessage = true;
            return;
        } else {
            this.showErrorMessage = false;
        }
        this.loaderService.show();
        const checked = event?.target?.checked; // ✅ REAL current value
        const control = this.termscheckForm.get('Attestation');

        const existing: any = control?.value;

        try {
            control?.setValue(checked, { emitEvent: false });

            if (checked) {
                await this.saveDocument();
            } else {
                await this.onDeleteDocument(docData);
            }

        } catch (error: any) {
            control?.setValue(existing, { emitEvent: false });
        } finally {
            this.loaderService.hide();
        }
    }

    async generateFile(fileName: any) {
        return new File([], fileName, {
            type: 'application/pdf',
            lastModified: Date.now()
        });
    }


    async onPdfScroll(event: any) {
        const element = event.target;

        const requiredResult = await this.updateSignatureInputState();
        this.applyAutoPatchedDomLocks();
        const atBottom =
            element.scrollTop + element.clientHeight >= element.scrollHeight - 5;

        if (atBottom) {
            this.isPdfRead = true;
            await this.clearHighlights();
            await this.highlightMissingFields(requiredResult.missingKeys, false);
        }
    }

    private scrollPdfToBottom(): void {
        setTimeout(() => {
            const pdfViewerContainer =
                this.pdfContainer?.nativeElement?.querySelector(
                    '.ng2-pdf-viewer-container'
                ) as HTMLElement;

            if (pdfViewerContainer) {
                pdfViewerContainer.scrollTo({
                    top: pdfViewerContainer.scrollHeight,
                    behavior: 'smooth'
                });
                this.isPdfRead = true;
            }
        }, 100);
    }

    private scrollToSignaturePage(pageIndex: number): void {
        setTimeout(() => {
            const pdfViewerContainer =
                this.pdfContainer?.nativeElement?.querySelector(
                    '.ng2-pdf-viewer-container'
                ) as HTMLElement;

            const pageEl = this.getPageElByIndex(pageIndex);

            if (pdfViewerContainer && pageEl) {
                // Compute the page's top offset relative to the scrollable container
                const containerRect = pdfViewerContainer.getBoundingClientRect();
                const pageRect = pageEl.getBoundingClientRect();
                const scrollOffset =
                    pdfViewerContainer.scrollTop +
                    (pageRect.top - containerRect.top) -
                    (pdfViewerContainer.clientHeight / 2) +
                    (pageRect.height / 2);

                pdfViewerContainer.scrollTo({
                    top: Math.max(0, scrollOffset),
                    behavior: 'smooth'
                });
            } else if (pageEl) {
                // Fallback: if we cannot find the container, use scrollIntoView
                pageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 300);
    }

    private waitForPdfReady(): Promise<void> {
        return new Promise(resolve => {
            const check = () => {
                if (
                    this.pdfDoc &&
                    this.fieldIndexById.size > 0
                ) {
                    resolve();
                } else {
                    setTimeout(check, 200);
                }
            };

            check();
        });
    }

    enableAutoFormatting(): void {
        setTimeout(() => {
    
            // DOB
            const dobInput = document.querySelector(
                'input[name="Date of birth"]'
            ) as HTMLInputElement;
    
            if (dobInput && !(dobInput as any).__formatterAttached) {
    
                dobInput.addEventListener('input', (e: any) => {
                    let v = e.target.value.replace(/\D/g, '');
    
                    if (v.length > 2) {
                        v = v.substring(0, 2) + '/' + v.substring(2);
                    }
    
                    if (v.length > 5) {
                        v = v.substring(0, 5) + '/' + v.substring(5);
                    }
    
                    e.target.value = v.substring(0, 10);
                });
    
                (dobInput as any).__formatterAttached = true;
            }
    
            // Phone
            const phoneInput = document.querySelector(
                'input[name="Phone"]'
            ) as HTMLInputElement;
    
            if (phoneInput && !(phoneInput as any).__formatterAttached) {
    
                phoneInput.addEventListener('input', (e: any) => {
                    let v = e.target.value.replace(/\D/g, '');
    
                    if (v.length > 10) {
                        v = v.substring(0, 10);
                    }
    
                    if (v.length > 6) {
                        e.target.value =
                            `(${v.substring(0, 3)}) ${v.substring(3, 6)}-${v.substring(6)}`;
                    } else if (v.length > 3) {
                        e.target.value =
                            `(${v.substring(0, 3)}) ${v.substring(3)}`;
                    } else {
                        e.target.value = v;
                    }
                });
    
                (phoneInput as any).__formatterAttached = true;
            }
    
        }, 1000);
    }

    private captureCurrentDomValues(): void {
        try {
            const capturedKeys = new Set<string>();

            // Capture all text / textarea / select inputs currently rendered by the PDF viewer
            const inputs = this.pdfContainer?.nativeElement?.querySelectorAll(
                'input:not([type="checkbox"]):not([type="radio"]), textarea, select'
            ) as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

            inputs?.forEach((el) => {
                const name = el.getAttribute('name');
                if (!name) return;
                const value = (el as HTMLInputElement).value?.trim();
                if (value !== undefined && value !== null) {
                    PATCH_VALUES[name] = value;
                    capturedKeys.add(name);
                }
            });

            // Capture checkboxes and radio buttons separately
            const checkboxes = this.pdfContainer?.nativeElement?.querySelectorAll(
                'input[type="checkbox"], input[type="radio"]'
            ) as NodeListOf<HTMLInputElement>;

            checkboxes?.forEach((el) => {
                const name = el.getAttribute('name');
                if (!name) return;
                if (el.type === 'radio') {
                    // For radio groups, only capture the checked value
                    if (el.checked) {
                        PATCH_VALUES[name] = el.value || 'On';
                        capturedKeys.add(name);
                    }
                } else {
                    // For checkboxes, capture the checked state
                    if (el.checked) {
                        PATCH_VALUES[name] = el.value || 'On';
                        capturedKeys.add(name);
                    }
                }
            });

            this.log('captureCurrentDomValues: captured', Object.keys(PATCH_VALUES).length, 'total fields');
        } catch (e) {
            // Non-fatal: if capture fails, fall back to existing PATCH_VALUES
            console.warn('captureCurrentDomValues failed silently:', e);
        }
    }

}
